import { getLocale } from 'umi';
import moment from 'moment';
import { getCountryNameByCountryCode } from '@intelligence/utils/commonUtils';
import { RegionsConfig } from './_config';

import type { IRegion } from '@intelligence/types/common';
import type {
  IReleaseDateByPlatformRes,
  IReleaseDateByPlatformFormatter,
  IReleaseDateByPlatformData,
} from '../types';

const getRegionName = (
  originalRegionCode: string,
  regionCode: string,
  cfg: {
    formatMessage: Function;
    regionOptions: IRegion[];
  },
) => {
  const { formatMessage, regionOptions } = cfg || {};
  if (regionCode === 'ww') return formatMessage({ id: 'dbi_world_wide' });
  if (regionCode === 'early access')
    return formatMessage({ id: 'dbi_early_access' });
  if (regionCode === 'full release')
    return formatMessage({ id: 'dbi_full_release' });

  const regionName = getCountryNameByCountryCode(
    regionOptions,
    regionCode,
    'region',
  );
  // regionName === regionCode 说明getCountryNameByCountryCode方法没匹配上，需要展示原本的region，否则展示匹配上的regionName
  return regionName === regionCode ? originalRegionCode : regionName;
};

const analysisReleaseDateInfo = (
  data: {
    [key: string]: string;
  },
  cfg: {
    formatMessage: Function;
    regionOptions: IRegion[];
  },
): string[] => {
  try {
    return Object.entries(data)
      .sort((a, b) => {
        const aM = moment(a?.[1]);
        const bM = moment(b?.[1]);
        if (!aM.isValid() && !bM.isValid()) return 0;
        if (!aM.isValid()) return 1;
        if (!bM.isValid()) return -1;
        return aM.isAfter(bM) ? 1 : -1;
      })
      .map(([region, date]) => {
        const regionCode = region?.toLowerCase() || '';
        const regionName = getRegionName(region, regionCode, cfg);
        return `${date} - ${regionName}`;
      });
  } catch (exception) {
    return [];
  }
};
const analysisReleaseDateArray = (
  data: string[][],
  cfg: {
    formatMessage: Function;
    regionOptions: IRegion[];
  },
): string[] => {
  try {
    return data.map(([region, date]) => {
      const regionCode = region?.toLowerCase() || '';
      const regionName = getRegionName(region, regionCode, cfg);
      return `${date} - ${regionName}`;
    });
  } catch (exception) {
    return [];
  }
};
export const defaultFormatter: IReleaseDateByPlatformFormatter<
  IReleaseDateByPlatformRes[]
> = (
  list,
  cfg: {
    formatMessage: Function;
    regionOptions: IRegion[];
  },
) => {
  if (!Array.isArray(list)) return [];
  return list.map((item) => ({
    ...item,
    release_date: Array.isArray(item?.release_date)
      ? analysisReleaseDateArray(item?.release_date, cfg)
      : analysisReleaseDateInfo(item?.release_date, cfg),
  }));
};

export function getRegionNameByLocale(region: string): string {
  const locale = getLocale();
  const isCn = locale === 'zh-CN';
  if (typeof region !== 'string') return '';

  const currentRegion: { en: string; cn: string; } = RegionsConfig[region];
  if (currentRegion) {
    return isCn ? currentRegion.cn : currentRegion.en;
  }
  return region;
}

const getReleaseRegionOrTypeName = (
  regionOrType: string,
  formatMessage: Function,
) => {
  const lowercaseRegionOrStatus = regionOrType?.toLowerCase() || '';

  // 发行样式
  if (lowercaseRegionOrStatus === 'early access')
    return formatMessage({ id: 'dbi_early_access' });
  if (lowercaseRegionOrStatus === 'full release')
    return formatMessage({ id: 'dbi_full_release' });
  if (lowercaseRegionOrStatus === 'offline')
    return formatMessage({ id: 'dbi_off_line' });
  if (lowercaseRegionOrStatus === 'beta')
    return formatMessage({ id: 'dbi_beta' });

  // 发行区域
  return getRegionNameByLocale(regionOrType);
};

type TReleaseDateFormatter = (
  list: IReleaseDateByPlatformData[],
  formatMessage: Function,
) => IReleaseDateByPlatformData[];

export const releaseDateFormatter: TReleaseDateFormatter = (
  list,
  formatMessage,
) => {
  if (!Array.isArray(list)) return [];

  return list.map((item) => ({
    ...item,
    release_info: item.release_info?.map((releaseItem) => ({
      ...releaseItem,
      status: getReleaseRegionOrTypeName(releaseItem.status, formatMessage),
      country: getReleaseRegionOrTypeName(releaseItem.country, formatMessage),
    })),
  }));
};
